import { DateTime } from 'luxon';
import {
    type QuestionOption,
    type QuestionSchema,
    QuestionSchemaSchema,
    type QuestionType,
    type SelectOption,
} from '../types/templateFormFields';

export const INTAKE_DATE_DISPLAY_FORMAT = 'MMM dd yyyy';

export interface MultiSelectOption {
    optionLabel: string;
    optionValue: string | number;
}

export interface IntakeSelectOption {
    selectItemLabel: string;
    selectItemValue: string | number;
}

export interface IntakeTableColumnInput {
    key?: string;
    label?: string;
    type?: string;
    selectOptions?: IntakeSelectOption[];
    options?: MultiSelectOption[];
}

export type QuestionSchemaInput = QuestionSchema | string | null | undefined;

export function parseQuestionSchema(questionSchema?: QuestionSchemaInput): QuestionSchema | null {
    if (!questionSchema) return null;

    if (typeof questionSchema === 'object') {
        return questionSchema;
    }

    try {
        const parsed = JSON.parse(questionSchema);
        const result = QuestionSchemaSchema.safeParse(parsed);
        return result.success ? result.data : null;
    } catch {
        return null;
    }
}

export function getQuestionSchemaType(
    questionSchema?: QuestionSchemaInput,
): QuestionType | undefined {
    return parseQuestionSchema(questionSchema)?.type;
}

export function toMultiSelectOptions(options?: QuestionOption[]): MultiSelectOption[] | undefined {
    if (!options?.length) return undefined;

    const normalized = options.flatMap((option) => {
        const { optionLabel, optionValue } = option;
        if (
            typeof optionLabel !== 'string' ||
            (typeof optionValue !== 'string' && typeof optionValue !== 'number')
        ) {
            return [];
        }

        return [{ optionLabel, optionValue }];
    });

    return normalized.length > 0 ? normalized : undefined;
}

export function parseTableMultiSelectOptions(
    options?: QuestionOption[],
): MultiSelectOption[] | undefined {
    return toMultiSelectOptions(options);
}

export function toSelectOptions(options?: SelectOption[]): IntakeSelectOption[] | undefined {
    if (!options?.length) return undefined;

    const normalized = options.flatMap((option) => {
        const { selectItemLabel, selectItemValue } = option;
        if (
            typeof selectItemLabel !== 'string' ||
            (typeof selectItemValue !== 'string' && typeof selectItemValue !== 'number')
        ) {
            return [];
        }

        return [{ selectItemLabel, selectItemValue }];
    });

    return normalized.length > 0 ? normalized : undefined;
}

export function getIntakeTableColumns(
    questionSchema?: QuestionSchemaInput,
): IntakeTableColumnInput[] {
    const columns = parseQuestionSchema(questionSchema)?.columns;
    if (!columns?.length) {
        return [];
    }

    return columns.flatMap((column) => {
        if (column.type === 'upload') {
            return [];
        }

        return [
            {
                key: column.key,
                label: column.label.trim() ? column.label : column.key,
                type: column.type,
                options: toMultiSelectOptions(column.options),
                selectOptions: column.selectOptions?.map((option) => ({
                    selectItemLabel: option.selectItemLabel,
                    selectItemValue: option.selectItemValue,
                })),
            },
        ];
    });
}

export function parseMultiSelectValue(value: string | string[] | null | undefined): string[] {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map(String);
    }
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed.map(String);
        }
        return [];
    } catch {
        return [];
    }
}

export function formatMultiSelectValue(value: unknown, options?: MultiSelectOption[]): string {
    const selectedValues = parseMultiSelectValue(value as string | string[] | null | undefined);
    if (selectedValues.length === 0) return '';

    return selectedValues
        .map((selectedValue) => {
            const option = options?.find((item) => String(item.optionValue) === selectedValue);
            return option?.optionLabel ?? selectedValue;
        })
        .join(', ');
}

function isJsonStringArrayValue(value: unknown): boolean {
    if (typeof value !== 'string') return false;

    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) return false;

    try {
        const parsed = JSON.parse(trimmed);
        return (
            Array.isArray(parsed) &&
            parsed.length > 0 &&
            parsed.every((item) => typeof item === 'string' || typeof item === 'number')
        );
    } catch {
        return false;
    }
}

function shouldFormatAsMultiSelect(value: unknown, questionSchema?: QuestionSchemaInput): boolean {
    const schemaType = getQuestionSchemaType(questionSchema);
    return schemaType === 'multiselect' || (!schemaType && isJsonStringArrayValue(value));
}

function formatSingleOptionLabel(
    value: unknown,
    options?: { label: string; value: string | number }[],
): string {
    if (value === null || value === undefined) return '';

    const renderedValue = String(value).trim();
    if (!renderedValue) return '';

    const optionLabel = options?.find((option) => String(option.value) === renderedValue)?.label;

    return optionLabel ?? renderedValue;
}

export function formatIntakeCurrencyDisplayValue(value: unknown): string {
    if (value === null || value === undefined) return '';

    const renderedValue = String(value).trim();
    if (!renderedValue) return '';

    const numericValue =
        typeof value === 'number'
            ? value
            : Number.parseFloat(renderedValue.replace(/[^0-9.-]/g, ''));

    if (Number.isNaN(numericValue)) {
        return renderedValue;
    }

    return `$${numericValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

export function formatIntakeSelectDisplayValue(
    value: unknown,
    selectOptions?: IntakeSelectOption[],
): string {
    return formatSingleOptionLabel(
        value,
        selectOptions?.map((option) => ({
            label: option.selectItemLabel,
            value: option.selectItemValue,
        })),
    );
}

export function formatIntakeRadioDisplayValue(
    value: unknown,
    options?: MultiSelectOption[],
): string {
    return formatSingleOptionLabel(
        value,
        options?.map((option) => ({
            label: option.optionLabel,
            value: option.optionValue,
        })),
    );
}

export function formatIntakeDateDisplayValue(value: unknown): string {
    if (value === null || value === undefined) return '';

    const renderedValue = String(value).trim();
    if (!renderedValue) return '';

    const isoDate = DateTime.fromISO(renderedValue);
    if (isoDate.isValid) {
        return isoDate.toFormat(INTAKE_DATE_DISPLAY_FORMAT);
    }

    const legacyDate = DateTime.fromFormat(renderedValue, INTAKE_DATE_DISPLAY_FORMAT);
    if (legacyDate.isValid) {
        return legacyDate.toFormat(INTAKE_DATE_DISPLAY_FORMAT);
    }

    return renderedValue;
}

export function formatIntakeTableCellDisplayValue(
    value: unknown,
    column: IntakeTableColumnInput,
): string {
    if (column.type === 'multiselect') {
        return formatMultiSelectValue(value, column.options);
    }

    if (Array.isArray(value)) {
        return value.map(String).filter(Boolean).join(', ');
    }

    if (value === null || value === undefined) {
        return '';
    }

    const renderedValue = String(value).trim();
    if (!renderedValue) {
        return '';
    }

    if (column.type === 'date') {
        return formatIntakeDateDisplayValue(renderedValue);
    }

    if (column.type === 'currency') {
        return formatIntakeCurrencyDisplayValue(renderedValue);
    }

    if (column.type === 'select') {
        return formatIntakeSelectDisplayValue(renderedValue, column.selectOptions);
    }

    const selectOptionLabel = column.selectOptions?.find(
        (option) => String(option.selectItemValue) === renderedValue,
    )?.selectItemLabel;

    return selectOptionLabel ?? renderedValue;
}

export interface IntakeTableDisplayValue {
    columns: IntakeTableColumnInput[];
    rows: string[][];
}

function parseIntakeTableRows(value: unknown): Record<string, unknown>[] {
    if (typeof value !== 'string' || !value.trim()) return [];

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed)
            ? parsed.filter((row) => !!row && typeof row === 'object' && !Array.isArray(row))
            : [];
    } catch {
        return [];
    }
}

export function formatIntakeTableDisplayValue(
    value: unknown,
    questionSchema?: QuestionSchemaInput,
): IntakeTableDisplayValue {
    const columns = getIntakeTableColumns(questionSchema);
    const rows = parseIntakeTableRows(value).flatMap((row) => {
        const renderedRow = columns.map((column) =>
            formatIntakeTableCellDisplayValue(row[column.key ?? ''], column),
        );
        return renderedRow.some(Boolean) ? [renderedRow] : [];
    });

    return { columns, rows };
}

export function formatContactFormFieldDisplayValue(key: string, value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (key === 'dateOfBirth') {
        return formatIntakeDateDisplayValue(value);
    }

    return String(value).trim();
}

function formatIntakeFieldDisplayValueBySchemaType(
    value: unknown,
    questionSchema?: QuestionSchemaInput,
): string | undefined {
    const schema = parseQuestionSchema(questionSchema);
    const schemaType = getQuestionSchemaType(questionSchema);

    if (schemaType === 'date') {
        return formatIntakeDateDisplayValue(value);
    }

    if (schemaType === 'currency') {
        return formatIntakeCurrencyDisplayValue(value);
    }

    if (schemaType === 'select') {
        return formatIntakeSelectDisplayValue(value, toSelectOptions(schema?.selectOptions));
    }

    if (schemaType === 'radio') {
        return formatIntakeRadioDisplayValue(value, toMultiSelectOptions(schema?.options));
    }

    return undefined;
}

export function formatIntakeFieldDisplayValue(
    value: unknown,
    questionSchema?: QuestionSchemaInput,
): string {
    if (value === null || value === undefined) return '';

    if (shouldFormatAsMultiSelect(value, questionSchema)) {
        const schema = parseQuestionSchema(questionSchema);
        return formatMultiSelectValue(value, toMultiSelectOptions(schema?.options));
    }

    const formattedByType = formatIntakeFieldDisplayValueBySchemaType(value, questionSchema);
    if (formattedByType !== undefined) {
        return formattedByType;
    }

    return String(value);
}

export function formatIntakeFieldDisplayValuePreservingType(
    value: unknown,
    questionSchema?: QuestionSchemaInput,
): unknown {
    if (value === null || value === undefined) return value;

    if (shouldFormatAsMultiSelect(value, questionSchema)) {
        const schema = parseQuestionSchema(questionSchema);
        return formatMultiSelectValue(value, toMultiSelectOptions(schema?.options));
    }

    const formattedByType = formatIntakeFieldDisplayValueBySchemaType(value, questionSchema);
    if (formattedByType !== undefined) {
        return formattedByType;
    }

    return value;
}
